/**
 * Repaxx Collection — public tracker feed (CSV or JSON)
 * Fetches a view-only public feed URL configured in theme settings.
 * Never embed private Google credentials — public CSV/JSON only.
 */
(function () {
  'use strict';

  /** @param {string} value */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * @param {string} text
   * @returns {string[][]}
   */
  function parseCsv(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          cell += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          cell += char;
        }
        continue;
      }

      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        row.push(cell);
        cell = '';
      } else if (char === '\n' || (char === '\r' && next === '\n')) {
        row.push(cell);
        cell = '';
        if (row.some((c) => c.trim() !== '')) {
          rows.push(row);
        }
        row = [];
        if (char === '\r') i++;
      } else if (char !== '\r') {
        cell += char;
      }
    }

    row.push(cell);
    if (row.some((c) => c.trim() !== '')) {
      rows.push(row);
    }

    return rows;
  }

  /**
   * @param {unknown} data
   * @returns {{ headers: string[], rows: string[][], updated?: string }}
   */
  function normalizeJson(data) {
    /** @type {string | undefined} */
    let updated;
    /** @type {Record<string, unknown>[]} */
    let objects = [];

    if (Array.isArray(data)) {
      objects = data;
    } else if (data && typeof data === 'object') {
      const record = /** @type {{ rows?: unknown; updated?: unknown }} */ (data);
      if (Array.isArray(record.rows)) {
        objects = record.rows;
      }
      if (typeof record.updated === 'string') {
        updated = record.updated;
      }
    }

    if (!objects.length) {
      return { headers: [], rows: [] };
    }

    const headers = Object.keys(objects[0]);
    const rows = objects.map((obj) => headers.map((key) => String(obj[key] ?? '')));
    return { headers, rows, updated };
  }

  class RepaxxCollectionTracker {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.feedUrl = root.dataset.feedUrl || '';
      this.refreshSeconds = Number(root.dataset.refreshSeconds) || 45;
      this.statusEl = root.querySelector('[data-repaxx-tracker-status]');
      this.tableWrap = root.querySelector('[data-repaxx-tracker-table-wrap]');
      this.thead = root.querySelector('[data-repaxx-tracker-thead]');
      this.tbody = root.querySelector('[data-repaxx-tracker-tbody]');
      this.updatedEl = root.querySelector('[data-repaxx-tracker-updated]');
      this.refreshBtn = root.querySelector('[data-repaxx-tracker-refresh]');
      this.timerId = null;
      this.isLoading = false;

      if (!this.feedUrl) return;

      this.refreshBtn?.addEventListener('click', () => this.load());
      this.load();
      this.timerId = window.setInterval(() => this.load(), this.refreshSeconds * 1000);
    }

    /** @param {string} message */
    showStatus(message) {
      if (!this.statusEl) return;
      this.statusEl.hidden = false;
      this.statusEl.innerHTML = `<p class="repaxx-live-tracker__message">${escapeHtml(message)}</p>`;
      if (this.tableWrap) this.tableWrap.hidden = true;
    }

    /** @param {string} message */
    showError(message) {
      if (!this.statusEl) return;
      this.statusEl.hidden = false;
      this.statusEl.innerHTML = `<p class="repaxx-live-tracker__error" role="alert">${escapeHtml(message)}</p>`;
      if (this.tableWrap) this.tableWrap.hidden = true;
    }

    /**
     * @param {string[]} headers
     * @param {string[][]} rows
     */
    renderTable(headers, rows) {
      if (!this.thead || !this.tbody || !this.tableWrap || !this.statusEl) return;

      if (!headers.length || !rows.length) {
        this.showStatus('No tracker rows are available yet.');
        return;
      }

      const headerHtml = headers.map((h) => `<th scope="col">${escapeHtml(h)}</th>`).join('');
      this.thead.innerHTML = `<tr>${headerHtml}</tr>`;

      const bodyHtml = rows
        .map((row, index) => {
          const cells = headers
            .map((_, colIndex) => `<td>${escapeHtml(row[colIndex] ?? '')}</td>`)
            .join('');
          const rowClass = index % 2 === 1 ? ' class="repaxx-tracker-table__row--alt"' : '';
          return `<tr${rowClass}>${cells}</tr>`;
        })
        .join('');

      this.tbody.innerHTML = bodyHtml;
      this.statusEl.hidden = true;
      this.tableWrap.hidden = false;
    }

  /** @param {string} [updated] */
    setUpdated(updated) {
      if (!this.updatedEl) return;
      const stamp = updated || new Date().toLocaleString();
      this.updatedEl.textContent = `Last updated: ${stamp}`;
      this.updatedEl.hidden = false;
    }

    async load() {
      if (this.isLoading || !this.feedUrl) return;
      this.isLoading = true;
      if (this.refreshBtn) this.refreshBtn.disabled = true;

      try {
        const response = await fetch(this.feedUrl, {
          method: 'GET',
          credentials: 'omit',
          cache: 'no-store',
        });

        if (!response.ok) {
          throw new Error(`Tracker feed unavailable (${response.status}).`);
        }

        const contentType = response.headers.get('content-type') || '';
        const text = await response.text();

        if (contentType.includes('json') || text.trim().startsWith('{') || text.trim().startsWith('[')) {
          const data = JSON.parse(text);
          const { headers, rows, updated } = normalizeJson(data);
          this.renderTable(headers, rows);
          this.setUpdated(updated);
        } else {
          const parsed = parseCsv(text);
          if (parsed.length < 2) {
            this.showStatus('No tracker rows are available yet.');
            this.setUpdated();
            return;
          }
          const [headers, ...rows] = parsed;
          this.renderTable(headers, rows);
          this.setUpdated();
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Unable to load collection tracker data.';
        this.showError(message);
      } finally {
        this.isLoading = false;
        if (this.refreshBtn) this.refreshBtn.disabled = false;
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-repaxx-tracker]').forEach((root) => {
      if (root instanceof HTMLElement) {
        new RepaxxCollectionTracker(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
